// src/components/SimpleScheduler.jsx
import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faClock,
  faComments,
  faEnvelope
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import avatar from '../../images/avatar.jpeg';

/*──────────────── CONFIG ────────────────*/
const FORM_ENDPOINT         = 'https://formsubmit.co/info@onlinegrowthclub.nl';
const USE_FORMSUBMIT_BACKUP = false;

const SLOT_MIN   = 30;
const OPEN_HOUR  = 10;
const CLOSE_HOUR = 16;
const DAYS_AHEAD = 30;
const OWNER_PHONE_INTL = '31644109856';

/*────────── HELPERS ──────────*/
const getDaysOfMonth = (y, m) => {
  const out = [];
  const first = new Date(y, m, 1).getDay();
  const last  = new Date(y, m + 1, 0).getDate();
  for (let i = 0; i < first; i++) out.push(null);
  for (let d = 1; d <= last; d++) out.push(d);
  return out;
};

const getTimeSlots = () => {
  const tmp = [];
  for (let h = OPEN_HOUR; h < CLOSE_HOUR; h++) {
    const hh = String(h).padStart(2, '0');
    tmp.push(`${hh}:00`);
    if (SLOT_MIN === 30) tmp.push(`${hh}:30`);
  }
  return tmp;
};

const formatFullDate = d =>
  d.toLocaleDateString('nl-NL', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

/*──────────────── COMPONENT ─────────────*/
export default function SimpleScheduler() {
  /* STATE */
  const today = new Date();
  const [viewMonth, setViewMonth]       = useState(today.getMonth());
  const [viewYear,  setViewYear]        = useState(today.getFullYear());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [appointmentType, setAppointmentType] = useState('');

  const [firstName, setFirstName] = useState('');
  const [lastName,  setLastName]  = useState('');
  const [email,     setEmail]     = useState('');
  const [phone,     setPhone]     = useState('');
  const [website,   setWebsite]   = useState('');

  const days  = getDaysOfMonth(viewYear, viewMonth);
  const slots = getTimeSlots();

  /* HELPERS */
  const changeMonth = dir => {
    let m = viewMonth + dir, y = viewYear;
    if (m < 0)  { m = 11; y--; }
    if (m > 11) { m = 0;  y++; }
    setViewMonth(m); setViewYear(y);
  };

  const disableDay = d => {
    if (d === null) return true;
    const date = new Date(viewYear, viewMonth, d);
    const diff = (date.setHours(0,0,0,0) - today.setHours(0,0,0,0)) / 864e5;
    return date.getDay() === 0 || diff < 0 || diff > DAYS_AHEAD;
  };

  const ready = selectedDate && selectedTime && appointmentType &&
                firstName && lastName && email && phone;

  const buildMessage = () =>
    [
      'Hallo,',
      '',
      'Ik wil graag een afspraak inplannen.',
      '',
      `Type afspraak: ${appointmentType}`,
      `Voornaam: ${firstName}`,
      `Achternaam: ${lastName}`,
      `E-mail: ${email}`,
      `Telefoon: ${phone}`,
      `Website: ${website || '-'}`,
      `Datum: ${formatFullDate(selectedDate)}`,
      `Tijd: ${selectedTime}`,
      '',
      'Met vriendelijke groet,'
    ].join('\n');

  const resetForm = () => {
    setSelectedDate(null); setSelectedTime(''); setAppointmentType('');
    setFirstName(''); setLastName(''); setEmail(''); setPhone(''); setWebsite('');
  };

  /* SENDERS */
  const handleEmail = async () => {
    if (!ready) return;
    const subject = encodeURIComponent('Afspraak aanvraag');
    const body    = encodeURIComponent(buildMessage());
    window.location.href =
      `mailto:info@onlinegrowthclub.nl?subject=${subject}&body=${body}`;

    if (USE_FORMSUBMIT_BACKUP) {
      try {
        const fd = new FormData();
        fd.append('Afspraaktype', appointmentType);
        fd.append('Voornaam', firstName);
        fd.append('Achternaam', lastName);
        fd.append('Email', email);
        fd.append('Telefoon', phone);
        fd.append('Website', website || '-');
        fd.append('Datum', formatFullDate(selectedDate));
        fd.append('Tijd', selectedTime);
        fd.append('_subject', 'Afspraak aanvraag');
        fd.append('_replyto', email);
        fd.append('_template', 'table');
        fd.append('_captcha', 'false');
        await fetch(FORM_ENDPOINT, { method: 'POST', body: fd, mode: 'no-cors' });
      } catch (_) {/* silently ignore */}
    }
    resetForm();
  };

  const handleWhatsApp = () => {
    if (!ready) return;
    const text = encodeURIComponent(buildMessage());
    window.open(`https://wa.me/${OWNER_PHONE_INTL}?text=${text}`, '_blank');
    resetForm();
  };

  /* RENDER */
  return (
    <section className="booking-shell">
      {/* INFO */}
      <aside className="info-col">
        <div className="host">
          <img src={avatar} alt="Host avatar" className="avatar" />
        </div>
        <ul className="details">
          <li><FontAwesomeIcon icon={faClock} className="icon" /> 30&nbsp;min videocall</li>
          <li><FontAwesomeIcon icon={faComments} className="icon" /> Vragen&nbsp;/ offerte</li>
        </ul>
      </aside>

      {/* CALENDAR */}
      <div className="calendar-col">
        <header className="cal-head">
          <button onClick={() => changeMonth(-1)} aria-label="Vorige maand">
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <span className="month-title">
            {new Date(viewYear, viewMonth).toLocaleString('nl-NL', {
              month: 'long', year: 'numeric'
            })}
          </span>
          <button onClick={() => changeMonth(1)} aria-label="Volgende maand">
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </header>

        <div className="cal-grid">
          {['zo','ma','di','wo','do','vr','za'].map(d => <div key={d} className="dow">{d}</div>)}

          {days.map((d,i) => {
            const disabled = disableDay(d);
            const selected = selectedDate &&
              d === selectedDate.getDate() &&
              viewMonth === selectedDate.getMonth() &&
              viewYear  === selectedDate.getFullYear();
            return (
              <button
                key={i}
                disabled={disabled}
                className={`day ${disabled?'disabled':''} ${selected?'selected':''}`}
                onClick={() => !disabled && (setSelectedDate(new Date(viewYear,viewMonth,d)),setSelectedTime(''))}
              >
                {d || ''}
              </button>
            );
          })}
        </div>
      </div>

      {/* SLOTS & FORM */}
      <div className="slots-col">
        {selectedDate ? (
          <>
            <h4>{formatFullDate(selectedDate)}</h4>

            <div className="slots-grid">
              {slots.map(t => (
                <button
                  key={t}
                  className={`slot ${selectedTime===t ? 'active' : ''}`}
                  onClick={() => setSelectedTime(t)}
                >
                  {t}
                </button>
              ))}
            </div>

            {selectedTime && (
              <form className="contact-form" onSubmit={e=>e.preventDefault()} noValidate>
                <select value={appointmentType} onChange={e=>setAppointmentType(e.target.value)} required>
                  <option value="" disabled>Kies type afspraak *</option>
                  <option value="GROEISCAN">Gratis GROEISCAN</option>
                  <option value="Website demo">Website demo</option>
                  <option value="Webdesign Lease">Webdesign Lease</option>
                  <option value="Social Media Beheer Lease">Social Media Beheer Lease</option>
                  <option value="SEO Lease">SEO Lease</option>
                  <option value="SEA / Ads Lease">SEA / Ads Lease</option>
                  <option value="Branding Lease">Branding Lease</option>
                  <option value="Flyer Lease">Flyer Lease</option>
                  <option value="Oriëntatie gesprek">Oriëntatie gesprek</option>
                  <option value="Overig">Overig</option>
                </select>

                <div className="name-row">
                  <input placeholder="Voornaam *" value={firstName} onChange={e=>setFirstName(e.target.value)} required />
                  <input placeholder="Achternaam *" value={lastName} onChange={e=>setLastName(e.target.value)} required />
                </div>
                <input type="email" placeholder="E-mail *"   value={email}   onChange={e=>setEmail(e.target.value)} required />
                <input type="tel"   placeholder="Telefoon *" value={phone}   onChange={e=>setPhone(e.target.value)} required />
                <input             placeholder="Website (optioneel)" value={website} onChange={e=>setWebsite(e.target.value)} />

                <div className="send-methods">
                  <button type="button" className="confirm-btn whatsapp" disabled={!ready} onClick={handleWhatsApp}>
                    <FontAwesomeIcon icon={faWhatsapp} className="icon-btn" /> WhatsApp
                  </button>
                  <button type="button" className="confirm-btn email" disabled={!ready} onClick={handleEmail}>
                    <FontAwesomeIcon icon={faEnvelope} className="icon-btn" /> Mail
                  </button>
                </div>

                {/* INFO-VAK */}
                <div className="speed-note">
                  <FontAwesomeIcon icon={faClock} />
                  <p>
                    <strong>Let op:</strong> kies je voor <strong>WhatsApp</strong>, dan ontvang je doorgaans
                    binnen <strong>1 werkdag</strong> reactie. Kies je voor <strong>e-mail</strong>,
                    dan kun je binnen <strong>3 werkdagen</strong> antwoord verwachten.
                  </p>
                </div>
              </form>
            )}
          </>
        ) : (
          <p className="pick-hint">Kies eerst een datum →</p>
        )}
      </div>
    </section>
  );
}
